import { useEffect, useMemo, useState } from 'react'
import Breadcrumb from '../../../core/common/Breadcrumb/breadcrumb'
import { Link } from 'react-router-dom'
import StudentSidebar from '../common/studentSidebar'
import ProfileCard from '../common/profileCard'
import {
  getUserTransactions,
  getUserTransactionDetail,
  UserTransaction,
  UserTransactionDetail,
} from '../../../api/user'
import {
  checkTransactionStatus,
  CheckTransactionStatusRequest,
} from '../../../api/transaction'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'

const StudentOrder = () => {
  // Data order from API
  const [orders, setOrders] = useState<UserTransaction[]>([])
  const [loading, setLoading] = useState(true)
  const [
    selectedTransaction,
    setSelectedTransaction,
  ] = useState<UserTransactionDetail | null>(null)
  const [transactionLoading, setTransactionLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [checkingStatus, setCheckingStatus] = useState<{
    [key: string]: boolean
  }>({})

  const fetchOrders = async () => {
    setLoading(true)
    try {
      const res = await getUserTransactions()
      setOrders(res.transactions)
    } catch (error) {
      // Optionally handle error
      setOrders([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchOrders()
  }, [])

  const handleViewTransaction = async (transactionId: number) => {
    setTransactionLoading(true)
    try {
      const res = await getUserTransactionDetail(transactionId.toString())
      setSelectedTransaction(res.data)
    } catch (error) {
      console.error('Error fetching transaction details:', error)
      setSelectedTransaction(null)
    } finally {
      setTransactionLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('id-ID', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZoneName: 'short',
    })
  }

  const formatRupiah = (amount: number | string) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount
    if (isNaN(numAmount)) return 'Rp 0'

    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numAmount)
  }

  const toReadable = (value?: string | null) => {
    if (!value) return '-'
    const raw = value.toString().trim()
    const normalized = raw
      .toLowerCase()
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()

    // Explicit mappings for known types
    switch (normalized) {
      case 'live teaching':
        return 'Live Teaching'
      case 'cbt':
        return 'CBT'
      case 'course':
        return 'E-Course'
      default:
        return raw
          .replace(/[_-]+/g, ' ')
          .trim()
          .toLowerCase()
          .replace(/\b\w/g, (c) => c.toUpperCase())
    }
  }

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-success'
      case 'pending':
        return 'bg-warning'
      case 'failed':
        return 'bg-danger'
      default:
        return 'bg-info'
    }
  }

  const handleCheckStatus = async (orderId: string) => {
    setCheckingStatus((prev) => ({ ...prev, [orderId]: true }))
    try {
      const payload: CheckTransactionStatusRequest = { order_id: orderId }
      const response = await checkTransactionStatus(payload)

      // Show success message
      alert(`Status transaksi berhasil dicek: ${response.data.status}`)

      // Refetch orders to get the latest data
      await fetchOrders()
    } catch (error) {
      console.error('Error checking transaction status:', error)
      alert(
        'Terjadi kesalahan saat mengecek status transaksi. Silakan coba lagi.',
      )
    } finally {
      setCheckingStatus((prev) => ({ ...prev, [orderId]: false }))
    }
  }

  const generateInvoicePDF = async () => {
    if (!selectedTransaction) return

    try {
      // Get the invoice modal content
      const invoiceElement = document.getElementById('invoice-content')
      if (!invoiceElement) return

      // Create canvas from HTML with zoom out
      const canvas = await html2canvas(invoiceElement, {
        scale: 1.5, // Reduced scale for zoom out effect
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: invoiceElement.scrollWidth,
        height: invoiceElement.scrollHeight,
      })

      const imgData = canvas.toDataURL('image/png')
      const pdf = new jsPDF('p', 'mm', 'a4') // 'p' for portrait orientation

      // Calculate dimensions for portrait A4
      const pageWidth = 210 // A4 portrait width in mm
      const pageHeight = 297 // A4 portrait height in mm
      const margin = 10 // margin in mm
      const contentWidth = pageWidth - margin * 2 // 190mm

      // Calculate image dimensions to fit content width
      const imgWidth = contentWidth
      const imgHeight = (canvas.height * imgWidth) / canvas.width

      let heightLeft = imgHeight
      let position = margin

      // Add first page
      pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight)
      heightLeft -= pageHeight - margin * 2

      // Add additional pages if needed
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight + margin
        pdf.addPage()
        pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight)
        heightLeft -= pageHeight - margin * 2
      }

      // Download the PDF
      const fileName = `Invoice-${selectedTransaction.order_id}.pdf`
      pdf.save(fileName)
    } catch (error) {
      console.error('Error generating PDF:', error)
      alert('Terjadi kesalahan saat membuat PDF. Silakan coba lagi.')
    }
  }

  const filteredOrders = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    return orders.filter((order) => {
      // status filter
      if (statusFilter && order.status !== statusFilter) return false

      // text search filter
      if (!term) return true
      const fields: Array<string | undefined | null> = [
        order.order_id,
        order.email,
        order.status,
        order.total?.toString(),
        order.course?.title,
        order.course?.type,
      ]
      return fields
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(term))
    })
  }, [orders, searchTerm, statusFilter])

  const handleConfirmTransfer = (orderId: number) => {
    const order = orders.find((o) => o.id === orderId);
    
    if (!order) return alert('Order not found.')

    const adminNumber = '6285213489432'
    const courseList = order.course.title

    const message = `
Halo Admin PT. Jadi Praktisi Indonesia 👋

Saya ingin konfirmasi pembayaran manual.

Nama: ${order.user.fullname}
Email: ${order.email}

Kursus yang dibeli:
${courseList}

Total: Rp${order.total.toLocaleString()}

Mohon untuk segera dikonfirmasi ya 🙏
  `.trim()

    const encodedMessage = encodeURIComponent(message)
    const waUrl = `https://wa.me/${adminNumber}?text=${encodedMessage}`

    window.location.href = waUrl
  }

  return (
    <>
      <Breadcrumb title="Order History" />
      <div className="content">
        <div className="container">
          {/* profile box */}
          <ProfileCard />
          {/* profile box */}
          <div className="row">
            {/* sidebar */}
            <StudentSidebar />
            {/* sidebar */}
            <div className="col-lg-9">
              <div className="page-title d-flex align-items-center justify-content-between">
                <h5>Order History</h5>
              </div>
              <div className="row">
                <div className="col-md-8">
                  <div className="mb-3">
                    <div className="dropdown">
                      <Link
                        to="#"
                        className="dropdown-toggle btn rounded border d-inline-flex align-items-center"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                      >
                        Status:{' '}
                        {statusFilter
                          ? statusFilter.charAt(0).toUpperCase() +
                          statusFilter.slice(1)
                          : 'All'}
                      </Link>
                      <ul className="dropdown-menu dropdown-menu-end p-3">
                        <li>
                          <Link
                            to="#"
                            className="dropdown-item rounded-1"
                            onClick={(e) => {
                              e.preventDefault()
                              setStatusFilter('')
                            }}
                          >
                            All
                          </Link>
                        </li>
                        <li>
                          <Link
                            to="#"
                            className="dropdown-item rounded-1"
                            onClick={(e) => {
                              e.preventDefault()
                              setStatusFilter('paid')
                            }}
                          >
                            Paid
                          </Link>
                        </li>
                        <li>
                          <Link
                            to="#"
                            className="dropdown-item rounded-1"
                            onClick={(e) => {
                              e.preventDefault()
                              setStatusFilter('pending')
                            }}
                          >
                            Pending
                          </Link>
                        </li>
                        <li>
                          <Link
                            to="#"
                            className="dropdown-item rounded-1"
                            onClick={(e) => {
                              e.preventDefault()
                              setStatusFilter('failed')
                            }}
                          >
                            Failed
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="input-icon mb-3">
                    <span className="input-icon-addon">
                      <i className="isax isax-search-normal-14" />
                    </span>
                    <input
                      type="text"
                      className="form-control form-control-md"
                      placeholder="Search"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
              </div>
              <div className="table-responsive custom-table">
                {loading ? (
                  <div className="text-center p-4">Loading...</div>
                ) : (
                  <table className="table">
                    <thead className="thead-light">
                      <tr>
                        <th>Order ID</th>
                        <th>Date</th>
                        <th>Content</th>
                        <th>Content Type</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOrders.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="text-center">
                            No orders found.
                          </td>
                        </tr>
                      ) : (
                        filteredOrders.map((order) => (
                          <tr key={order.id}>
                            <td>
                              <Link
                                to="#"
                                className="text-primary"
                                data-bs-toggle="modal"
                                data-bs-target="#view_invoice"
                                onClick={() => handleViewTransaction(order.id)}
                              >
                                {order.order_id}
                              </Link>
                            </td>
                            <td>{formatDate(order.created_at)}</td>
                            <td>{order.course?.title || '-'}</td>
                            <td>{toReadable(order.course?.type)}</td>
                            <td>{formatRupiah(order.total)}</td>
                            <td>
                              <span
                                className={`badge ${getStatusBadgeClass(
                                  order.status,
                                )} d-inline-flex align-items-center me-1`}
                              >
                                <i className="fa-solid fa-circle fs-5 me-1" />
                                {order.status}
                              </span>
                            </td>
                            <td>
                              {order.status === 'pending' ? (
                                <div className="d-flex gap-2 flex-wrap">
                                  {(order as any)?.redirect_url && (
                                    <a
                                      href={(order as any).redirect_url}
                                      className="btn btn-primary btn-sm rounded-pill"
                                    >
                                      Lanjutkan Pembayaran
                                    </a>
                                  )}
                                  {(order as any)?.type === 'manual' ? (
                                    <>
                                      <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm rounded-pill"
                                        onClick={() =>
                                          handleConfirmTransfer(Number(order.id))
                                        }
                                      >
                                        Konfirmasi Pembayaran
                                      </button>
                                    </>) :
                                    (
                                      <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm rounded-pill"
                                        onClick={() =>
                                          handleCheckStatus(order.order_id)
                                        }
                                        disabled={
                                          checkingStatus[order.order_id] || false
                                        }
                                      >
                                        {checkingStatus[order.order_id] ? (
                                          <>
                                            <span
                                              className="spinner-border spinner-border-sm me-1"
                                              role="status"
                                              aria-hidden="true"
                                            ></span>
                                            Mengecek...
                                          </>
                                        ) : (
                                          'Cek Status'
                                        )}
                                      </button>
                                    )}
                                </div>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* View Invoice */}
      <div className="modal fade" id="view_invoice">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5>Invoice</h5>
              <div className="d-flex align-items-center gap-2">
                {selectedTransaction && (
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={generateInvoicePDF}
                  >
                    <i className="fa-solid fa-download me-1" />
                    Download PDF
                  </button>
                )}
                <button
                  type="button"
                  className="btn-close custom-btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <i className="isax isax-close-circle5" />
                </button>
              </div>
            </div>
            <div className="modal-body">
              {transactionLoading ? (
                <div className="text-center p-4">
                  Loading transaction details...
                </div>
              ) : selectedTransaction ? (
                <div id="invoice-content">
                  <div className="border-bottom mb-3">
                    <div className="row justify-content-between align-items-center flex-wrap row-gap-4">
                      <div className="col-md-6">
                        <div className="mb-2 invoice-logo-white">
                          <img
                            src="/assets/img/logo-color.webp"
                            className="img-fluid"
                            alt="logo"
                            style={{
                              maxHeight: '50px',
                            }}
                          />
                        </div>
                        <p className="mb-2">
                          PT. Jadi Praktisi Indonesia, Bandar Lampung -
                          Indonesia
                        </p>
                      </div>
                      <div className="col-md-6">
                        <div className=" text-end mb-3">
                          <h6 className="text-default mb-1 text-secondary fs-16">
                            #{selectedTransaction.order_id}
                          </h6>
                          <p className="mb-1">
                            Created Date :{' '}
                            <span className="text-gray-9">
                              {formatDate(selectedTransaction.created_at)}
                            </span>{' '}
                          </p>
                          <p>
                            Updated Date :{' '}
                            <span className="text-gray-9">
                              {formatDate(selectedTransaction.updated_at)}
                            </span>{' '}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="border-bottom mb-3">
                    <div className="row g-4">
                      <div className="col-lg-5">
                        <span className="mb-3 d-flex">From</span>
                        <div>
                          <h6 className="mb-2 fs-16">
                            PT. Jadi Praktisi Indonesia
                          </h6>
                          <p className="fs-14 mb-1">
                            Bandar Lampung - Indonesia
                          </p>
                          <p className="fs-14 mb-1">
                            Email : jadipraktisicom@gmail.com
                          </p>
                          <p className="fs-14">Phone : +62 851-5689-7824</p>
                        </div>
                      </div>
                      <div className="col-lg-5">
                        <span className="mb-3 d-flex">To</span>
                        <div>
                          <h6 className="mb-2">{selectedTransaction.user ? selectedTransaction.user.fullname : 'Student'}</h6>
                          <p className="fs-14 mb-1">
                            Email : {selectedTransaction.email}
                          </p>
                          <p className="fs-14 mb-1">
                            Transaction Type :{' '}
                            {toReadable(selectedTransaction.type)}
                          </p>
                        </div>
                      </div>
                      <div className="col-lg-2">
                        <div className="mb-3 text-end">
                          <span className="mb-1 d-block">Payment Status</span>
                          <span
                            className={`badge ${getStatusBadgeClass(
                              selectedTransaction.status,
                            )} badge-sm d-inline-flex align-items-center fs-10 fw-normal mb-4`}
                          >
                            <i className="fa-solid fa-circle fs-5 me-1" />
                            {selectedTransaction.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="table-responsive custom-table rounded-0">
                      <table className="table">
                        <thead className="thead-light">
                          <tr>
                            <th className="w-50">Description</th>
                            <th className="text-center">Qty</th>
                            <th className="text-end">Cost</th>
                            {/* <th className="text-end">Discount</th> */}
                            <th className="text-end">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>
                              <p className="text-gray-9 text-wrap">
                                {selectedTransaction.course?.title || 'Course'}
                              </p>
                            </td>
                            <td className="text-gray text-center">1</td>
                            <td className="text-gray text-end">
                              {formatRupiah(selectedTransaction.total)}
                            </td>
                            {/* <td className="text-gray text-end">Rp0</td> */}
                            <td className="text-gray text-end">
                              {formatRupiah(selectedTransaction.total)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="border-bottom mb-3 pb-3">
                    <div className="row">
                      <div className="col-md-6" />
                      <div className="col-md-6">
                        <div className="d-flex justify-content-between align-items-center border-bottom my-2 pb-2 pe-3">
                          <p className="text-gray mb-0">Sub Total</p>
                          <p className="text-gray-9 fw-medium">
                            {formatRupiah(selectedTransaction.total)}
                          </p>
                        </div>
                        {/* <div className="d-flex justify-content-between align-items-center border-bottom my-2 pb-2 pe-3">
                          <p className="mb-0">Discount (0%)</p>
                          <p className="text-gray-9 fs-14 fw-medium">Rp0</p>
                        </div>
                        <div className="d-flex justify-content-between align-items-center mb-3 pe-3">
                          <p className="mb-0">VAT (0%)</p>
                          <p className="text-gray-9 fs-14 fw-medium mb-2">$0</p>
                        </div> */}
                        <div className="d-flex justify-content-between align-items-center mb-2 pe-3">
                          <h6 className="fs-16">Total Amount</h6>
                          <h6 className="fs-16">
                            {formatRupiah(selectedTransaction.total)}
                          </h6>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row align-items-center gy-3">
                    <div className="col-lg-8">
                      <div>
                        <h6 className="mb-1 fs-16">Terms and Conditions</h6>
                        <p>
                          Catatan: Kami tidak mengeluarkan kwitansi/invoice
                          secara cetak, silahkan print dokumen ini sebagai bukti
                          yang sah untuk keperluan kawan praktisi. Konfirmasi
                          Pembayaran: +62 852-1348-9432 (Whatsapp)
                        </p>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="text-end">
                        <h6 className="fs-15 pe-3 mb-2">
                          PT. Jadi Praktisi Indonesia
                        </h6>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center p-4">
                  No transaction details available.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* /View Invoice */}
    </>
  )
}

export default StudentOrder
